/**
 * Month-view lane packing for tentpole bands — a string-YMD port of the
 * creator calendar's splitCalendarWeeks/assignCalendarLanes
 * (cg-client-pages/components/calendar/calendarModel.ts) so both surfaces
 * render tentpole windows the same way. All comparisons are lexicographic
 * on YYYY-MM-DD strings, matching the rest of this directory.
 */

import { Tentpole } from './types';
import { tentpoleLabel } from './shared';

export interface TentpoleSegment {
  tentpole: Tentpole;
  startCol: number; // 0..6 within the week
  endCol: number; // 0..6, >= startCol
  lane: number; // 0-based row within the week's band area
  continuesLeft: boolean; // window starts before this week
  continuesRight: boolean; // window ends after this week
}

const pad2 = (value: number): string => String(value).padStart(2, '0');

const dateToYmd = (date: Date): string =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

/**
 * Sunday-first weeks of real YMDs covering the month — out-of-month days
 * included as real dates (not null padding) so bands can span across them
 * the way the creator calendar's grid does.
 */
export function splitMonthWeeks(monthKey: string): string[][] {
  const y = parseInt(monthKey.slice(0, 4), 10);
  const m = parseInt(monthKey.slice(5, 7), 10);
  const first = new Date(y, m - 1, 1);
  const last = new Date(y, m, 0);
  const start = new Date(y, m - 1, 1 - first.getDay());
  const end = new Date(y, m - 1, last.getDate() + (6 - last.getDay()));
  const weeks: string[][] = [];
  for (let cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 7)) {
    weeks.push(
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(cursor);
        d.setDate(d.getDate() + i);
        return dateToYmd(d);
      }),
    );
  }
  return weeks;
}

/** Last day of the month, 'YYYY-MM-DD' — mirrors the creator calendar's
 *  calendarMonthEndYmd so both surfaces scope tentpoles to the same range. */
export const monthEndYmd = (monthKey: string): string => {
  const y = parseInt(monthKey.slice(0, 4), 10);
  const m = parseInt(monthKey.slice(5, 7), 10);
  return dateToYmd(new Date(y, m, 0));
};

/** Active tentpoles whose window overlaps [rangeStart, rangeEnd] — the
 *  creator-page visibility rule (inactive never reaches a calendar grid). */
export const tentpolesForRange = (
  tentpoles: Tentpole[],
  rangeStart: string,
  rangeEnd: string,
): Tentpole[] =>
  tentpoles.filter((t) => t.active && t.startDate <= rangeEnd && t.endDate >= rangeStart);

/** Tentpoles active on a given day — feeds the day-detail modal. */
export const tentpolesOnDay = (tentpoles: Tentpole[], ymd: string): Tentpole[] =>
  tentpoles.filter((t) => t.startDate <= ymd && t.endDate >= ymd);

/**
 * Greedy lane packing for one week (creator-calendar parity): clip each
 * window to the week, sort by start column, then longer-first, then display
 * label (the creator sorts its emoji-prefixed labels, so ties must break on
 * the same key), and place into the first lane free at that column.
 */
export function assignTentpoleLanes(
  tentpoles: Tentpole[],
  week: string[],
): { segments: TentpoleSegment[]; laneCount: number } {
  const weekStart = week[0];
  const weekEnd = week[6];
  const candidates = tentpoles
    .filter((t) => t.startDate <= weekEnd && t.endDate >= weekStart)
    .map((t) => {
      const startCol = t.startDate <= weekStart ? 0 : Math.max(0, week.indexOf(t.startDate));
      const endCol = t.endDate >= weekEnd ? 6 : Math.max(startCol, week.indexOf(t.endDate));
      return {
        tentpole: t,
        startCol,
        endCol,
        continuesLeft: t.startDate < weekStart,
        continuesRight: t.endDate > weekEnd,
      };
    })
    .sort(
      (a, b) =>
        a.startCol - b.startCol ||
        b.endCol - a.endCol ||
        tentpoleLabel(a.tentpole).localeCompare(tentpoleLabel(b.tentpole)),
    );

  const laneEnds: number[] = [];
  const segments = candidates.map((segment) => {
    let lane = laneEnds.findIndex((endCol) => endCol < segment.startCol);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(segment.endCol);
    } else {
      laneEnds[lane] = segment.endCol;
    }
    return { ...segment, lane };
  });
  return { segments, laneCount: laneEnds.length };
}
